import { useTranslation } from 'react-i18next';
import { Swipe } from 'shared/ui/animations';
import styles from './style.module.scss';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { UIButton, UITypography } from 'shared/ui';
import { TOKEN_ICONS, BOOSTER_ICONS } from 'shared/constants';

import BobeCoin from 'assets/images/bobe-coin.png';
import JustGiftIcon from 'assets/images/just-gift.webp';
import QuantityIcon from 'assets/icons/quantity.png';
import MainButton from 'assets/images/main-button.png';
import EnergyCapacity from 'assets/images/energy-capacity.webp';
import backIcon from 'assets/icons/back.svg';
import infoIcon from 'assets/icons/info.svg';
import ovensImage from 'assets/images/ovens.webp';
import botsImage from 'assets/images/bots.webp';
import chestsImage from 'assets/images/chests.webp';

import { useMarketStore, useGlobalStore, useRatesStore } from 'shared/store';
import { buyMarketItem, createInvoice, getPaymentStatus } from 'shared/api';
import { useAlert } from 'shared/ui/components/alert/alert';
import { Spinner } from 'components/loading/spinner';
import inInventoryIcon from 'assets/icons/in_inventory.svg';

import { Address, Cell, beginCell, toNano } from '@ton/core';
import { useTonAddress } from '@tonconnect/ui-react';

import { JettonWallet } from 'shared/wrappers/jetton-wallet';
import { useTonConnect, useWalletConnectModal } from 'shared/hooks';
import { modalService } from 'shared/ui/components/modal/modal-purchase-service';
import { alertService } from 'shared/ui/components/alert/service';
import { paymentSelectionModalService, UsdtPaymentSelectionModal } from './choose-usdt-payment';

export type Currency = 'usdt' | 'stars' | 'ton';

export const getCurrencyIcon = (currency: Currency) => {
  switch (currency) {
    case 'stars':
      return TOKEN_ICONS.stars;
    case 'ton':
      return TOKEN_ICONS.ton;
    default:
      return TOKEN_ICONS.usdt;
  }
};

export const formatPrice = (
  usdt: number,
  currency: Currency,
  rates: { stars: number; ton: number },
) => {
  switch (currency) {
    case 'stars':
      return Math.round(usdt / rates.stars);
    case 'ton':
      return Number((usdt / rates.ton).toFixed(1));
    default:
      return Number(usdt.toFixed(1));
  }
};

export const JETTON_TRANSFER_GAS_FEES = toNano('0.038');

// export const USDT_MASTER_ADDRESS = Address.parse(
//   'kQD0GKBM8ZbryVk2aESmzfU6b9b_8era_IkvBSELujFZPsyy',
// );

const {
  address: USDT_MASTER_ADDRESS,
  isBounceable: isBounceable,
  isTestOnly: isTestOnly,
} = Address.parseFriendly('kQD0GKBM8ZbryVk2aESmzfU6b9b_8era_IkvBSELujFZPsyy');
export { USDT_MASTER_ADDRESS };

export const INVOICE_WALLET_ADDRESS = Address.parse(
  '0QB_HuTmdqndryylH9q0AufgRel-04eyrUlw2DvyeIQibEyK',
);

// Polling function (used by all payment methods)
export const pollPaymentStatus = async (
  paymentId: any,
  updateUser: any,
  onPurchaseSuccess: any,
  setIsLoading: any,
  t: any,
) => {
  let attempts = 0;
  const maxAttempts = 60; // 5 минут (60 * 5 секунд)

  const checkStatus = async () => {
    try {
      attempts++;
      const response = await getPaymentStatus(paymentId);
      const result = response.data;

      if (result.success) {
        alertService.close();

        const purchasedItem = result.user_data.inventory.find(
          (item: any) => item.uuid === result.payment.item_id,
        );

        modalService.showPurchaseSuccess(purchasedItem);
        updateUser(result.user_data);
        onPurchaseSuccess?.();
        setIsLoading(false);
        return;
      } else {
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          alertService.show(`${t('v2.market.payment_processing')}${paymentId}`);
          setIsLoading(false);
        }
      }
    } catch (error) {
      // Error handling
    }
  };

  setTimeout(checkStatus, 5000);
};

// I opened a ticket for why i'm using this: https://github.com/ton-community/tma-usdt-payments-demo/issues/12
function getUserJettonWalletAddress(ownerAddress: any, jettonMasterAddress: any) {
  const JETTON_WALLET_CODE = Cell.fromBoc(
    Buffer.from(
      'b5ee9c7201020f010003d1000114ff00f4a413f4bcf2c80b01020162020302f8d001d0d3030171b08e48135f038020d721ed44d0d303fa00fa40fa40d104d31f01840f218210178d4519ba0282107bdd97deba12b1f2f48040d721fa003012a0401303c8cb0358fa0201cf1601cf16c9ed54e0fa40fa4031fa0031f401fa0031fa00013170f83a02d31f012082100f8a7ea5ba8e85303459db3ce03304050201200d0e01f203d33f0101fa00fa4021fa4430c000f2e14ded44d0d303fa00fa40fa40d15309c7052471b0c00021b1f2ad522bc705500ab1f2e0495115a120c2fff2aff82a54259070546004131503c8cb0358fa0201cf1601cf16c921c8cb0113f40012f400cb00c920f9007074c8cb02ca07cbffc9d004fa40f401fa00200602d0228210178d4519ba8e84325adb3ce034218210595f07bcba8e843101db3ce032208210eed236d3ba8e2f30018040d721d303d1ed44d0d303fa00fa40fa40d1335142c705f2e04a403303c8cb0358fa0201cf1601cf16c9ed54e06c218210d372158cbadc840ff2f00809019820d70b009ad74bc00101c001b0f2b19130e2c88210178d451901cb1f500a01cb3f5008fa0223cf1601cf1626fa025007cf16c9c8801801cb055004cf1670fa024063775003cb6bccccc945370700b42191729171e2f839206e938124279120e2216e94318128739101e25023a813a0738103a370f83ca00270f83612a00170f836a07381040982100966018070f837a0bcf2b0048050fb005803c8cb0358fa0201cf1601cf16c9ed5403f4ed44d0d303fa00fa40fa40d12372b0c002f26d07d33f0101fa005141a004fa40fa4053bac705f82a5464e070546004131503c8cb0358fa0201cf1601cf16c921c8cb0113f40012f400cb00c9f9007074c8cb02ca07cbffc9d0500cc7051bb1f2e04a09fa0021925f04e30d26d70b01c000b393306c33e30d55020a0b0c01f2ed44d0d303fa00fa40fa40d106d33f0101fa00fa40f401d15141a15288c705f2e04926c2fff2afc882107bdd97de01cb1f5801cb3f01fa0221cf1658cf16c9c8801801cb0526cf1670fa02017158cb6accc903f839206e943081169fde718102f270f8380170f836a0811a7770f836a0bcf2b0028050fb00030c0060c882107362d09c01cb1f2501cb3f5004fa0258cf1658cf16c9c8801001cb0524cf1658fa02017158cb6accc98011fb00007a5054a1f82fa07381040982100966018070f837b60972fb02c8801001cb055005cf1670fa027001cb6a8210d53276db01cb1f5801cb3fc9810082fb0059002003c8cb0358fa0201cf1601cf16c9ed540027bfd8176a2686981fd007d207d206899fc15209840021bc508f6a2686981fd007d207d2068af81c',
      'hex',
    ),
  )[0];

  // Правильная структура данных для Jetton-with-governance (USDT)
  const data = beginCell()
    .storeUint(0, 4) // status - 4 бита (0 = активный/незаблокированный)
    .storeCoins(0) // баланс - 0 для нового кошелька
    .storeAddress(ownerAddress)
    .storeAddress(jettonMasterAddress)
    // Без ссылки на код
    .endCell();

  // Create StateInit
  const stateInit = beginCell()
    .storeBit(0) // split_depth
    .storeBit(0) // special
    .storeBit(1) // code
    .storeRef(JETTON_WALLET_CODE)
    .storeBit(1) // data
    .storeRef(data)
    .storeBit(0) // library
    .endCell();

  const userJettonWalletAddress = new Address(0, stateInit.hash());
  return userJettonWalletAddress;
}

// payment
export const makePaymentHandler = (
  activeTab: string,
  t: any,
  updateUser: any,
  tonData: {
    tonConnectUI: any;
    userAddress: any;
    sender: any;
    walletAddress: any;
    tonClient: any;
  },
  currentUser?: any,
  currentLanguage?: string,
  openWalletModal?: () => void,
) => {
  return async (item: any, setIsLoading: any, onPurchaseSuccess: any) => {
    let paymentType = activeTab;

    // Если выбран USDT, показываем модалку и ждем выбора
    if (activeTab === 'usdt') {
      // Get item name in current language
      const lang = currentLanguage || 'en';
      const itemName = item.properties?.name?.[lang] || item.properties?.name?.en || 'Item';

      const selectedType = await paymentSelectionModalService.showPaymentSelection({
        itemName: itemName,
        price: Number(item.price_amount),
        userBalance: Number(currentUser?.usdt || 0),
      });

      // Если пользователь закрыл модалку без выбора
      if (!selectedType) {
        return;
      }

      // Устанавливаем тип платежа на основе выбора
      paymentType = selectedType;
    }

    // if (activeTab === 'usdt_jetton') {
    if (paymentType === 'usdt_balance') {
      // USDT in-game balance payment
      setIsLoading(true);
      try {
        const response = await buyMarketItem({
          currency: 'usdt',
          type: item.type,
          level: item.level,
        });
        const updatedUser = response.data;
        updateUser(updatedUser as any);

        // Show success modal with the item we already have
        modalService.showPurchaseSuccess(item);

        onPurchaseSuccess?.();
      } catch (error: any) {
        let errorMsg = t('v2.market.purchase_error');
        if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        }
        alertService.show(errorMsg);
      } finally {
        setIsLoading(false);
      }
      // } else if (paymentType === 'usdt') {
    } else if (paymentType === 'usdt_crypto') {
      // USDT Jetton payment (TON blockchain)
      const { tonClient, walletAddress, sender, userAddress } = tonData;

      if (!userAddress) {
        // Instead of showing alert, directly open wallet connection modal
        // NOTE: This only opens the modal. The actual wallet status listener
        // and backend sync is handled by WithdrawAmountModal in AppHeader.
        // Make sure WithdrawAmountModal is always rendered in the app!
        if (openWalletModal) {
          openWalletModal();
        }
        return;
      }
      setIsLoading(true);
      try {
        if (!tonClient || !walletAddress) {
          alertService.show(t('v2.market.ton_connection_error'));
          return;
        }
        // Create invoice
        const invoiceRequest = await createInvoice({
          item_id: item.uuid,
          currency: 'usdt',
        });
        const invoice = invoiceRequest.data;

        // Get Jetton wallet address
        // const jettonMaster = tonClient.open(JettonMaster.create(USDT_MASTER_ADDRESS));
        const usersUsdtAddress = getUserJettonWalletAddress(walletAddress, USDT_MASTER_ADDRESS);

        // Create Jetton wallet instance
        const jettonWallet = tonClient.open(JettonWallet.createFromAddress(usersUsdtAddress));

        // Calculate amount in micro-USDT
        const usdtAmount = BigInt(Math.round(parseFloat(item.price_amount) * 1000000));

        // Send transfer
        await jettonWallet.sendTransfer(sender, {
          fwdAmount: BigInt(1),
          comment: invoice.comment,
          jettonAmount: usdtAmount,
          toAddress: INVOICE_WALLET_ADDRESS,
          value: JETTON_TRANSFER_GAS_FEES,
        });

        alertService.show(t('v2.market.transaction_sent'));
        pollPaymentStatus(invoice.payment_id, updateUser, onPurchaseSuccess, setIsLoading, t);
      } catch (error) {
        console.error('Ошибка при отправке транзакции:', error);
        alertService.show(t('v2.market.transaction_error'));
      } finally {
        setIsLoading(false);
      }
    } else if (paymentType === 'ton') {
      // TON payment logic
      const { tonConnectUI, userAddress } = tonData;

      if (!userAddress) {
        // Instead of showing alert, directly open wallet connection modal
        // NOTE: This only opens the modal. The actual wallet status listener
        // and backend sync is handled by WithdrawAmountModal in AppHeader.
        // Make sure WithdrawAmountModal is always rendered in the app!
        if (openWalletModal) {
          openWalletModal();
        }
        return;
      }

      setIsLoading(true);
      try {
        const invoiceRequest = await createInvoice({ item_id: item.uuid, currency: 'ton' });
        const invoice = invoiceRequest.data;

        const commentCell = beginCell()
          .storeUint(0, 32) // op code для текста
          .storeStringTail(`${invoice.comment}`)
          .endCell();

        const transaction = {
          validUntil: Date.now() + 5 * 60 * 1000,
          messages: [
            {
              address: invoice.recipient_address,
              amount: String(invoice.price_amount * 10 ** 9),
              payload: commentCell.toBoc().toString('base64'),
            },
          ],
        };

        const result = await tonConnectUI.sendTransaction(transaction);

        console.log('Transaction sent:', result);

        alertService.show(t('v2.market.transaction_sent'));

        pollPaymentStatus(invoice.payment_id, updateUser, onPurchaseSuccess, setIsLoading, t);
      } catch (error) {
        console.error('Transaction failed:', error);
        alertService.show(t('v2.market.transaction_error'));
      } finally {
        setIsLoading(false);
      }
    } else if (paymentType === 'stars') {
      // Telegram Stars payment
      setIsLoading(true);
      try {
        const invoiceRequest = await createInvoice({ item_id: item.uuid, currency: 'stars' });
        const invoice = invoiceRequest.data;

        Telegram.WebApp.openInvoice(invoice.invoice_url, function (status) {
          if (status === 'paid') {
            alertService.show(t('v2.market.transaction_sent'));
            pollPaymentStatus(invoice.payment_id, updateUser, onPurchaseSuccess, setIsLoading, t);
          } else if (status === 'failed') {
            // Обработка неудачного платежа
          } else if (status === 'cancelled') {
            // Пользователь отменил платеж
          }
        });
      } catch (error) {
        console.error('Transaction failed:', error);
        alertService.show(t('v2.market.transaction_error'));
      } finally {
        setIsLoading(false);
      }
    }
  };
};

export const CardMarketBase = ({ children, label = '', isActive = false }: any) => {
  return (
    <div className={`${styles.cardHolder}`}>
      <div
        className={`${styles.card} ${isActive ? styles.cardActive : ''} my-border my-dark-linear-bg`}
      >
        <div className={`${styles.label}`}>{label}</div>
        {children}
      </div>
    </div>
  );
};

export const ChestCardMarket = ({
  chest,
  onPurchaseSuccess,
  handlePayment,
  activeTab,
  rates,
}: any) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const { showAlert } = useAlert();
  const updateUser = useGlobalStore((state) => state.updateUser);

  const [isLoading, setIsLoading] = useState(false);
  // const toggleRefetchAppData = useGlobalStore((state) => state.toggleRefetchAppData);

  const {
    properties: { name, tokens, bonus_tokens, energy_charges, contents = [] },
    price_amount,
    level,
  } = chest;

  const icon = getCurrencyIcon(activeTab as any);
  const price = formatPrice(+price_amount, activeTab as any, rates);

  // const handleBuyItem = () => {
  //   setIsLoading(true);
  //   buyMarketItem({
  //     currency: 'usdt',
  //     type: 'chest',
  //     level: level,
  //   })
  //     .then((response) => {
  //       const updatedUser = response.data;
  //       updateUser(updatedUser as any);
  //       showAlert(t('v2.market.purchase_success'));

  //       onPurchaseSuccess?.();
  //     })
  //     .catch((error) => {
  //       let errorMsg = t('v2.market.purchase_error');
  //       if (error.response?.data?.detail) {
  //         errorMsg = error.response.data.detail;
  //       }
  //       showAlert(errorMsg);
  //     })
  //     .finally(() => setIsLoading(false));
  // };

  const chestName = name?.[currentLanguage] || name?.en;
  const totalTokens = tokens || 0; // + (bonus_tokens || 0);

  return (
    <CardMarketBase label={chestName}>
      <div className={styles.cardStructure}>
        <div className={styles.topContent}>
          {/* Left side - chest image */}
          <div className={styles.imageContainer}>
            <img
              src={`chests_market/${level}.webp`}
              alt={chestName}
              className={styles.chestImage}
            />
          </div>

          {/* Right side - chest info */}
          <div className={styles.infoContainer}>
            {/* Characteristics section */}
            <div className={styles.characteristics}>
              <h3>{t('v2.market.card.characteristics')}</h3>

              {/* Tokens row */}
              <div className={styles.statRow}>
                <div className={styles.statLabel}>
                  <div className="">{t('v2.market.card.tokens')}</div>
                  <img
                    src={BobeCoin}
                    alt=""
                    className={``}
                    width={16}
                    height={16}
                    style={{ display: 'block' }}
                  />
                </div>
                <div className={styles.statValue}>
                  <div>{totalTokens}</div>
                </div>
              </div>

              {/* Energy row */}
              <div className={styles.statRow}>
                <div className={styles.statLabel}>
                  <div className="">{t('v2.market.card.energy_charges')}</div>
                  <img
                    src={TOKEN_ICONS.energyCharge}
                    alt=""
                    className={``}
                    width={16}
                    height={16}
                    style={{ display: 'block' }}
                  />
                </div>
                <div className={styles.statValue}>
                  <div>{energy_charges || 0}</div>
                </div>
              </div>
            </div>

            {/* Gift section */}
            {bonus_tokens > 0 && (
              <div className={styles.giftSection}>
                <img
                  src={JustGiftIcon}
                  alt=""
                  className={``}
                  width={23}
                  height={27}
                  style={{ display: 'block', marginRight: '6px' }}
                />
                <div>
                  <span className={styles.colored}>
                    {`+${bonus_tokens} ${t('v2.market.card.token_bonus_1')} `}
                  </span>
                  <span>{`${t('v2.market.card.token_bonus_2')}`}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contents preview */}
        <div
          className={`${styles.contentsPreview} ${contents.length > 4 ? styles.twoRows : styles.oneRow}`}
        >
          {Array.from({ length: contents.length > 4 ? 8 : 4 }).map((_, index) => (
            <div key={index} className={styles.contentItem}>
              {contents[index] && contents[index].skin_hero_level ? (
                <>
                  <img
                    src={`heroes/${contents[index].skin_hero_level}.webp`}
                    alt={`Hero skin, level ${contents[index].skin_hero_level}`}
                    width={70}
                    height={70}
                  />
                  {contents[index].quantity > 1 && (
                    <div
                      className={styles.quantity}
                      style={{ backgroundImage: `url(${QuantityIcon})` }}
                    >
                      ×{contents[index].quantity}
                    </div>
                  )}
                </>
              ) : (
                // Empty placeholder
                <div className={styles.emptySlot}></div>
              )}
            </div>
          ))}
        </div>

        {/* Buy button */}
        <div className={styles.buyButton}>
          <div
            className={styles.mainButton}
            style={{ backgroundImage: `url(${MainButton})` }}
            onClick={() => handlePayment(chest, setIsLoading, onPurchaseSuccess)}
          >
            <div className={styles.priceTag}>
              {isLoading ? (
                <Spinner size={18} />
              ) : (
                <>
                  <div className={styles.quantity}>{price}</div>
                  <img src={icon} alt="" width={13} height={13} style={{ display: 'block' }} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </CardMarketBase>
  );
};

export const SkinHeroCardMarket = ({
  hero,
  onPurchaseSuccess,
  handlePayment,
  activeTab,
  rates,
}: any) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [isLoading, setIsLoading] = useState(false);

  const {
    properties: {
      name,
      description,
      energy_charges = 0,
      daily_limit_bonus = 0,
      session_bonus_percent = 0,
      boosters = [] as any[],
    },
    price_amount,
    level,
  } = hero;

  const icon = getCurrencyIcon(activeTab as any);
  const price = formatPrice(+price_amount, activeTab as any, rates);

  // Get booster icon from BOOSTER_ICONS
  const getBoosterIcon = (code: string) => {
    return BOOSTER_ICONS[code] || BOOSTER_ICONS.booster_insects_x; // fallback
  };

  const heroName = name?.[currentLanguage] || name?.en;
  const heroDescription = description?.[currentLanguage] || description?.en;

  return (
    <CardMarketBase label={heroName}>
      <div className={styles.cardStructure}>
        <div className={styles.topContent}>
          {/* Left side - hero image */}
          <div className={styles.imageContainer}>
            <img src={`heroes_market/${level}.webp`} alt={heroName} className={styles.heroImage} />
          </div>

          {/* Right side - hero info */}
          <div className={styles.infoContainer}>
            {/* Description section */}
            <div className={styles.description}>
              <h3>{t('v2.market.card.description')}</h3>
              <p>{heroDescription}</p>
            </div>

            {/* Characteristics section */}
            <div className={styles.characteristics}>
              <h3>{t('v2.market.card.characteristics')}</h3>

              {/* Energy charges row */}
              <div className={styles.statRow}>
                <div className={`${styles.statLabel} ${styles.statLabel2} `}>
                  <div className="">{`${t('v2.market.card.energy_charges')}: ${energy_charges}`}</div>
                  <img
                    src={TOKEN_ICONS.energyCharge}
                    alt=""
                    width={18}
                    height={18}
                    style={{ display: 'block' }}
                  />
                </div>
              </div>
            </div>

            {/* Bonus information */}
            <div className={styles.giftSection}>
              <img
                src={JustGiftIcon}
                alt=""
                className={``}
                width={23}
                height={27}
                style={{ display: 'block', marginRight: '6px' }}
              />
              <div className={styles.heroGiftDescription}>
                <div>
                  <span
                    className={styles.colored}
                  >{`+${session_bonus_percent}% ${t('v2.market.card.hero_bonus_points_1')} `}</span>
                  <span>{`${t('v2.market.card.hero_bonus_points_2')}`}</span>
                </div>
                <div>
                  <span className={styles.colored}>{`+${daily_limit_bonus} `}</span>
                  <span>{`${t('v2.market.card.hero_bonus_limit')}`}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.lowerSectionHero}>
          {/* Bonus boosters */}
          <div className={styles.boosterSection}>
            <h3>{t('v2.market.card.hero_bonus_boosters_title')}</h3>
            <div className={styles.boosterList}>
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className={styles.boosterItem}>
                  {boosters[index] ? (
                    <>
                      <img
                        src={getBoosterIcon(boosters[index].code)}
                        alt={boosters[index].code}
                        width={48}
                        height={48}
                      />
                      <div
                        className={styles.quantity}
                        style={{ backgroundImage: `url(${QuantityIcon})` }}
                      >
                        ×{boosters[index].quantity}
                      </div>
                    </>
                  ) : (
                    <div className={styles.emptySlot}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Buy button */}
          <div className={styles.buyButton}>
            <div
              className={styles.mainButton}
              style={{ backgroundImage: `url(${MainButton})` }}
              onClick={() => handlePayment(hero, setIsLoading, onPurchaseSuccess)}
            >
              <div className={styles.priceTag}>
                {isLoading ? (
                  <Spinner size={18} />
                ) : (
                  <>
                    <div className={styles.quantity}>{price}</div>
                    <img src={icon} alt="" width={13} height={13} style={{ display: 'block' }} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardMarketBase>
  );
};

export const SkinOvenCardMarket = ({
  oven,
  isOwned,
  displayedEnergyCapacity,
  onPurchaseSuccess,
  handlePayment,
  activeTab,
  rates,
}: any) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const { showAlert } = useAlert();
  const updateUser = useGlobalStore((state) => state.updateUser);
  const [isLoading, setIsLoading] = useState(false);

  const {
    properties: {
      name,
      description,
      energy_capacity = 0,
      energy_charges = 0,
      exchange_discount = 0,
    },
    price_amount,
    level,
  } = oven;

  const icon = getCurrencyIcon(activeTab as any);
  const price = formatPrice(+price_amount, activeTab as any, rates);

  // const handleBuyItem = () => {
  //   setIsLoading(true);
  //   buyMarketItem({
  //     currency: 'usdt',
  //     type: 'skin_oven',
  //     level: level,
  //   })
  //     .then((response) => {
  //       const updatedUser = response.data;
  //       updateUser(updatedUser as any);
  //       showAlert(t('v2.market.purchase_success'));

  //       onPurchaseSuccess?.();
  //     })
  //     .catch((error) => {
  //       let errorMsg = t('v2.market.purchase_error');
  //       if (error.response?.data?.detail) {
  //         errorMsg = error.response.data.detail;
  //       }
  //       showAlert(errorMsg);
  //     })
  //     .finally(() => setIsLoading(false));
  // };

  const ovenName = name?.[currentLanguage] || name?.en;
  const ovenDescription = description?.[currentLanguage] || description?.en;

  return (
    <CardMarketBase label={ovenName} isActive={isOwned}>
      <div className={styles.cardStructure}>
        <div className={styles.topContent}>
          {/* Left side - oven image */}
          <div className={styles.imageContainer}>
            <img src={`ovens_market/${level}.webp`} alt={ovenName} className={styles.heroImage} />
          </div>

          {/* Right side - oven info */}
          <div className={styles.infoContainer}>
            {/* Description section */}
            <div className={styles.description}>
              <h3>{t('v2.market.card.description')}</h3>
              <p>
                {ovenDescription} <img src={`icons/arrow-down.svg`} alt="" className="" />
              </p>
            </div>

            {/* Characteristics section */}
            <div className={styles.characteristics}>
              <h3>{t('v2.market.card.characteristics')}</h3>

              {/* Energy capacity row */}
              <div className={styles.ovenStatRow}>
                <div className={styles.statHeader}>
                  <div className={styles.statName}>
                    {t('v2.bakery.energy_capacity')}
                    {/* <img src={EnergyCharge} alt="" width={16} height={16} /> */}
                  </div>
                  {/* <div className={styles.statValue}>{energy_capacity}</div> */}
                </div>

                <div className={`${styles.progressBarContainer} `}>
                  <div className={`${styles.progressBar2} my-dark-linear-bg`}>
                    <div className={styles.barProgressMain} style={{ width: `${100}%` }}>
                      <img src={EnergyCapacity} alt="" className={styles.barFieldProgress} />
                      <span
                        className={`${styles.barFieldProgress} ${styles.barLabelProgress} ${styles.barLabelProgressShift}`}
                      >
                        {displayedEnergyCapacity}
                      </span>
                    </div>

                    {/* <img src={EnergyCapacity} alt="" className={``}  width={16} height={16} style={{ display: 'block' }} /> */}
                  </div>
                </div>
              </div>

              {/* Energy charges row */}
              <div className={styles.ovenStatRow}>
                <div className={styles.statHeader}>
                  <div className={styles.statName}>{t('v2.bakery.energy_charge')}</div>
                  {/* <div className={styles.statValue}>+{energy_charges}</div> */}
                </div>

                <div className={`${styles.progressBarContainer} `}>
                  <div className={`${styles.progressBar2} my-dark-linear-bg`}>
                    <div className={styles.barProgressMain} style={{ width: `${100}%` }}>
                      <div
                        className={styles.chargeFilled}
                        style={{ width: `${(100 / 500) * 100}%` }}
                      >
                        <img
                          src={TOKEN_ICONS.energyCharge}
                          alt=""
                          className={styles.barFieldProgress}
                        />
                        <span className={`${styles.barFieldProgress} ${styles.barLabelProgress}`}>
                          {`+${energy_charges}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.lowerSectionHero}>
              {/* Bonus information */}
              <div
                className={styles.giftSection}
                style={{ visibility: exchange_discount > 0 ? 'visible' : 'hidden' }}
              >
                <img
                  src={JustGiftIcon}
                  alt=""
                  className={``}
                  width={23}
                  height={27}
                  style={{ display: 'block', marginRight: '6px' }}
                />
                <div className={styles.heroGiftDescription}>
                  <div>
                    <span className={styles.colored}>
                      {`+ ${t('v2.market.card.oven_exchange_discount')} ${exchange_discount}%`}
                    </span>
                  </div>
                  <div>
                    <span>{`${t('v2.market.card.oven_exchange_discount_text')}`}</span>
                  </div>
                </div>
              </div>

              {/* Buy button */}
              <div className={styles.buyButton}>
                <div
                  className={`${styles.mainButton} ${isOwned ? styles.disabled : ''}`}
                  style={{ backgroundImage: `url(${MainButton})` }}
                  onClick={() => !isOwned && handlePayment(oven, setIsLoading, onPurchaseSuccess)}
                >
                  <div className={styles.priceTag}>
                    {isOwned ? (
                      // <span className={styles.ownedText}>{t('v2.market.already_owned')}</span>
                      // <span className={styles.ownedText}>{'✔️'}</span> {`heroes_market/${level}.webp`}
                      <img src={inInventoryIcon} alt="" width={15} height={14} />
                    ) : isLoading ? (
                      <Spinner size={18} />
                    ) : (
                      <>
                        <div className={styles.quantity}>{price}</div>
                        <img
                          src={icon}
                          alt=""
                          width={13}
                          height={13}
                          style={{ display: 'block' }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardMarketBase>
  );
};

export const MarketCategoryCard = ({ title, description, image, onNavigate }: any) => {
  const { t } = useTranslation();

  return (
    <div className={`${styles.categoryCard} my-border my-dark-linear-bg`} onClick={onNavigate}>
      <div className={styles.categoryImage}>
        <img src={image} alt={title} />
      </div>

      <div className={styles.categoryPanel}>
        <div className={styles.categoryInfo}>
          <UITypography variant="H2" className={styles.categoryTitle}>
            {title}
          </UITypography>
          <UITypography className={styles.categoryDescription}>{description}</UITypography>
        </div>

        <UIButton onClick={onNavigate} className={`${styles.categoryButton}`}>
          {t('v2.common.navigate')}
        </UIButton>
      </div>
    </div>
  );
};

export const MarketScreen = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('usdt');
  const updateUser = useGlobalStore((state) => state.updateUser);
  const { t, i18n } = useTranslation();

  // TON wallet related
  const { sender, walletAddress, tonClient, tonConnectUI } = useTonConnect();
  const userAddress = useTonAddress();
  const { openWalletModal } = useWalletConnectModal();

  const { rates, fetchRates } = useRatesStore();

  useEffect(() => {
    fetchRates();
  }, []);

  const [activeCategory, setActiveCategory] = useState<string | null>(
    (location.state as any)?.category || null,
  );
  const marketItems = useMarketStore((state) => state.marketItems);
  const currentUser = useGlobalStore((state) => state.user);

  // Update activeCategory when location state changes
  useEffect(() => {
    if ((location.state as any)?.category) {
      setActiveCategory((location.state as any).category);
    }
  }, [location.state]);

  // Create payment handler
  const handlePayment = makePaymentHandler(
    activeTab,
    t,
    updateUser,
    {
      tonConnectUI,
      userAddress,
      sender,
      walletAddress,
      tonClient,
    },
    currentUser,
    i18n.language,
    openWalletModal,
  );

  // Вычислить данные для печей один раз
  const ownedOvens = currentUser?.inventory?.filter((item: any) => item.type === 'skin_oven') || [];

  const maxOwnedOvenLevel = currentUser?.selected_skin_oven || 0;
  const currentEnergyCapacity = currentUser?.oven_energy_capacity || 0;

  // Функция для проверки и вычисления данных печи
  const getOvenData = (ovenLevel: number, ovenEnergyCapacity: number) => {
    const isOwned = ownedOvens.some((owned: any) => owned.level === ovenLevel);
    const displayedEnergyCapacity = isOwned
      ? // ? ovenEnergyCapacity
        Math.max(currentEnergyCapacity, ovenEnergyCapacity)
      : //
        ovenLevel < maxOwnedOvenLevel
        ? '+0'
        : ovenEnergyCapacity;

    return {
      isOwned,
      displayedEnergyCapacity,
    };
  };

  console.log('marketItems: ', marketItems);

  const categories = [
    {
      id: 'ovens',
      title: t('v2.market.ovens'),
      description: (
        <div className={styles.categoryDescription}>
          {t('v2.market.ovens_description_1')}
          <img
            src={TOKEN_ICONS.energyCharge}
            alt="Energy"
            width={12}
            height={12}
            style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 1px' }}
          />
          {t('v2.market.ovens_description_2')}
          <img
            src={BobeCoin}
            alt="Token"
            width={12}
            height={12}
            style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 1px' }}
          />
        </div>
      ),
      image: ovensImage,
      type: 'skin_oven',
    },
    {
      id: 'bots',
      title: t('v2.market.bots'),
      description: t('v2.market.bots_description'),
      image: botsImage,
      type: 'skin_hero',
    },
    {
      id: 'chests',
      title: t('v2.market.chests'),
      description: (
        <div className={styles.categoryDescription}>
          {t('v2.market.chests_description_1')}
          <img
            src={TOKEN_ICONS.energyCharge}
            alt="Energy"
            width={12}
            height={12}
            style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 1px' }}
          />
          {t('v2.market.chests_description_2')}
          <img
            src={BobeCoin}
            alt="Token"
            width={12}
            height={12}
            style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 1px' }}
          />
          {t('v2.market.chests_description_3')}
        </div>
      ),
      image: chestsImage,
      type: 'chest',
    },
  ];

  // If a category is selected, show its items
  if (activeCategory) {
    const categoryData = categories.find((cat) => cat.id === activeCategory);

    return (
      <>
        <div className={styles.marketContainer}>
          <div className={styles.navigationHeader}>
            <button className={styles.backButton} onClick={() => setActiveCategory(null)}>
              <img
                src={backIcon}
                alt="<"
                width={30}
                height={30}
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              />
            </button>
            <span>{categoryData?.title}</span>
            <button className={styles.backButton} onClick={() => setActiveCategory(null)}>
              <img
                src={infoIcon}
                alt="<"
                width={30}
                height={30}
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              />
            </button>
          </div>

          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'usdt' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('usdt')}
              data-text="USDT"
            >
              <span>USDT</span>
              <img src={TOKEN_ICONS.usdt} alt="USDT" />
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'stars' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('stars')}
              data-text="Telegram stars"
            >
              <span>Telegram stars</span>
              <img src={TOKEN_ICONS.stars} alt="Stars" />
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'ton' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('ton')}
              data-text="TON"
            >
              <span>TON</span>
              <img src={TOKEN_ICONS.ton} alt="TON" />
            </button>
          </div>

          {/* Show items for the selected category */}
          {categoryData && (
            <Swipe from="right" direction="horizontal">
              <div className={`${styles.marketContainer} ${styles.marketContainer2}`}>
                {categoryData.id === 'chests' && (
                  <>
                    {Object.values((marketItems?.chest as any) || {}).map((item: any) => (
                      <ChestCardMarket
                        key={item.uuid}
                        chest={item}
                        onPurchaseSuccess={() => {
                          setActiveCategory(null);
                        }}
                        handlePayment={handlePayment}
                        activeTab={activeTab}
                        rates={rates}
                      />
                    ))}
                  </>
                )}

                {categoryData.id === 'bots' && (
                  <>
                    {Object.values((marketItems?.skin_hero as any) || {}).map((item: any) => (
                      <SkinHeroCardMarket
                        key={item.uuid}
                        hero={item}
                        onPurchaseSuccess={() => {
                          setActiveCategory(null);
                        }}
                        handlePayment={handlePayment}
                        activeTab={activeTab}
                        rates={rates}
                      />
                    ))}
                  </>
                )}

                {categoryData.id === 'ovens' && (
                  <>
                    {Object.values((marketItems?.skin_oven as any) || {}).map((item: any) => {
                      const ovenData = getOvenData(item.level, item.properties.energy_capacity);

                      return (
                        <SkinOvenCardMarket
                          key={item.uuid}
                          oven={item}
                          isOwned={ovenData.isOwned}
                          displayedEnergyCapacity={ovenData.displayedEnergyCapacity}
                          onPurchaseSuccess={() => {
                            setActiveCategory(null);
                          }}
                          handlePayment={handlePayment}
                          activeTab={activeTab}
                          rates={rates}
                        />
                      );
                    })}
                  </>
                )}
              </div>
            </Swipe>
          )}
        </div>

        <UsdtPaymentSelectionModal />
      </>
    );
  }

  // Otherwise show categories
  return (
    <div className={styles.marketContainer}>
      <div className={styles.navigationHeader}>
        <button
          className={styles.backButton}
          onClick={() => {
            console.log(1);
            setActiveCategory(null);
          }}
          style={{ visibility: activeCategory ? 'visible' : 'hidden' }}
        >
          <img
            src={backIcon}
            alt="<"
            width={30}
            height={30}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
        </button>
        <span>{t('v2.market.title')}</span>
        <button className={styles.backButton} onClick={() => setActiveCategory(null)}>
          <img
            src={infoIcon}
            alt="<"
            width={30}
            height={30}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
        </button>
      </div>

      <Swipe from="top" direction="vertical" initialTransparent={false}>
        <div className={`${styles.marketContainer} ${styles.marketContainer2}`}>
          {categories.map((category) => (
            <MarketCategoryCard
              key={category.id}
              title={category.title}
              description={category.description}
              image={category.image}
              onNavigate={() => setActiveCategory(category.id)}
            />
          ))}
        </div>
      </Swipe>
    </div>
  );
};
